import { getAllServices, executeAction } from '../base';
import API from '../../../api';
import {
  ServiceHubService,
  ServiceId,
  ActionId,
  ActionWithServiceId,
  ServiceHubServices,
  ServiceHubEventData,
  JSServiceInvocationData,
  ActionCallId,
  ActionEventType,
  ServiceHubError,
  ActionResponse,
  GeneralActionResponse,
  EmptyActionData,
  CallActionOption,
  ActionRequest,
} from './types';
import {
  StreamEventType,
  StreamChunk,
  StreamListener,
  StreamController,
} from './stream-types';
import { JSService, JSServiceID, JSServiceRegistry } from './js-service-types';
import { SystemParam } from 'types/index';
import { getSystemParam } from '../../platform';
import {
  type Logger,
  getLogger,
  postAppBridgeMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../../bridge';
import { compareVersions } from '@zoom/hybrid-jsbridge';
import { Notification } from '../../../utils/type';
import {
  CreateMailData,
  CreateTasksData,
  ViewTasksData,
  ShowScheduleMeetingData,
  SendMessageSkillData,
  GetTodayUpcomingMeetingListResponse,
  Services,
  SendMessageSkillResponse,
  DirectShareData,
  DirectShareCheckResponse,
  GetFrontZappDetailInfoResponse,
  NewDocData,
  GetCurrentOpenedWhiteboardInfoResponse,
  DocDraftPreviewData,
  TrackClientInteractData,
  ShowSettingPageData,
  NewWhiteboardData,
  PopoutMenuData,
  PopoutMenuResponse,
  ShowAlertData,
  ShowAlertResponse,
} from '../services';
import { genRandomId } from '../../utils';
import { toPascalCase } from '../../../utils';

const DEFAULT_TIMEOUT = 30_000;
const SERVICE_ID_MAP = {
  mail: 'mail-service',
  tasks: 'tasks-service',
  calendar: 'calendar-service',
  chat: 'chat-service',
  common: 'common-service',
  chatUI: 'chatui-service',
  zoomApp: 'zapp-service',
  doc: 'doc-service',
  whiteboard: 'whiteboard-service',
  telemetry: 'telemetry-service',
} as const;

const CorrectTips = {
  chatui: 'chatUI',
  'chat-ui': 'chatUI',
  'chat-Ui': 'chatUI',
  chatUi: 'chatUI',
  'zoom-App': 'zoomApp',
  'zoom-app': 'zoomApp',
  'zoom-Apps': 'zoomApp',
  'zoom-apps': 'zoomApp',
  zoomApps: 'zoomApp',
  zoomapps: 'zoomApp',
  apps: 'zoomApp',
};

type CorrectTips = typeof CorrectTips;
type CorrectTipsKey = keyof CorrectTips;

type ServiceChangedHandler = (
  notification: Notification<ServiceHubEventData<unknown>>
) => void;

type ServiceHubJSInvocationHandler = (
  notification: Notification<JSServiceInvocationData<unknown>>
) => Promise<void>;

class ServiceHub {
  private clientVersion: string = '0.0.0';
  private platform?: SystemParam['deviceOs'] | 'window';
  private allServices: Map<ServiceId, ActionWithServiceId[]> = new Map();
  private pendingActions: Map<
    ActionCallId,
    { resolve: (p: any) => void; reject: (e: Error) => void }
  > = new Map();
  private streamListeners: Map<
    ActionCallId,
    { listener: StreamListener<any>; controller: StreamController }
  > = new Map();
  private readonly initialized: Promise<void>;
  private onServiceChangedHandler: ServiceChangedHandler | undefined;
  private _handleServiceHubEvent: ServiceChangedHandler | undefined;
  private _handleJSServiceInvocation: ServiceHubJSInvocationHandler | undefined;
  private logger: Logger;
  private jsServiceIds: string[] = Object.values(JSServiceID);
  private jsActionResponseTracker: Map<string, boolean> = new Map<
    string,
    boolean
  >();

  // JS Service Registry
  private jsServiceRegistry: JSServiceRegistry = new Map();

  constructor() {
    this.initialized = this.initialize();
    this.logger = getLogger();
  }

  private async initialize(): Promise<void> {
    const systemParam = await getSystemParam();
    this.clientVersion = systemParam.clientVersion;
    this.platform =
      systemParam.deviceOs === 'windows' ? 'window' : systemParam.deviceOs;

    await this.refreshService();

    this._handleServiceHubEvent = this.handleServiceHubEvent.bind(this);
    this._handleJSServiceInvocation = this.handleJSServiceInvocation.bind(this);

    registerSpecificNotificationHandler(
      'ZoomServiceHubEvent',
      this._handleServiceHubEvent
    );

    // Register handler for JS service invocation
    registerSpecificNotificationHandler(
      'zmOnServiceHubJSAction',
      this._handleJSServiceInvocation
    );
  }

  /**
   * Handles streaming events from the native layer
   * This processes chunks of data from streaming responses
   */
  private handleStreamEvent(data: ServiceHubEventData<StreamChunk<unknown>>) {
    const { actionCallId, eventData: chunk } = data;

    if (!actionCallId) {
      this.logger.warn(
        '[@zoom/hybrid-jssdk] received a StreamEvent without actionCallId',
        data
      );
      return;
    }

    const streamHandler = this.streamListeners.get(actionCallId);
    if (streamHandler) {
      const { listener } = streamHandler;

      // Pass the chunk to the listener
      listener(chunk);

      // If stream is complete, clean up
      if (
        chunk.type === StreamEventType.StreamComplete ||
        chunk.type === StreamEventType.StreamError
      ) {
        this.streamListeners.delete(actionCallId);
      }
    }
  }

  /**
   * Handles invocation of JS services from native
   * This allows native code to call JavaScript functions
   */
  private async handleJSServiceInvocation(
    notification: Notification<JSServiceInvocationData<unknown>>
  ) {
    const { result } = notification;
    const { serviceId, actionId, actionCallId, data } = result;

    // Find the corresponding JS service and action
    const jsService = this.jsServiceRegistry.get(serviceId);
    if (!jsService) {
      this.logger.warn(
        `[@zoom/hybrid-jssdk] JS service ${serviceId} not registered`
      );
      this.sendJSServiceResponse(serviceId, actionId, actionCallId, {}, false);
      return;
    }

    const action = jsService.actions.find((a) => a.actionId === actionId);
    if (!action) {
      this.logger.warn(
        `[@zoom/hybrid-jssdk] JS action ${actionId} not found in service ${serviceId}`
      );
      this.sendJSServiceResponse(serviceId, actionId, actionCallId, {}, false);
      return;
    }

    try {
      // Execute the handler and send back the response
      const result = await action.handler({ data, actionCallId });
      this.sendJSServiceResponse(
        serviceId,
        actionId,
        actionCallId,
        result,
        true
      );
    } catch (error) {
      const err = error as Error;
      this.logger.error(
        `[@zoom/hybrid-jssdk] JS action ${serviceId}/${actionId} execution failed:`,
        err
      );
      this.sendJSServiceResponse(serviceId, actionId, actionCallId, {}, false);
    }
  }

  /**
   * Send response back to native for JS service calls
   */
  private sendJSServiceResponse(
    serviceId: ServiceId,
    actionId: ActionId,
    actionCallId: string,
    result: any,
    success: boolean
  ) {
    postAppBridgeMessage(API.jsServiceResponse, {
      serviceId,
      actionId,
      actionCallId,
      result,
      success,
    });
  }

  private async handleServiceHubEvent(
    notification: Notification<ServiceHubEventData<unknown>>
  ) {
    const { result: eventData } = notification;

    if (this.isActionCompletionEvent(eventData.type)) {
      this.handleActionCompletion(eventData.actionCallId, eventData);
    } else if (this.isServiceStateChangeEvent(eventData.type)) {
      await this.refreshService();
      this.onServiceChangedHandler?.(notification);
    } else if (eventData.type === ActionEventType.ActionStreamResponse) {
      this.handleStreamEvent(
        eventData as ServiceHubEventData<StreamChunk<unknown>>
      );
    }
  }

  private isActionCompletionEvent(type: ActionEventType): boolean {
    return (
      type === ActionEventType.ActionCompleted ||
      type === ActionEventType.ActionFailed
    );
  }

  private isServiceStateChangeEvent(type: ActionEventType): boolean {
    return (
      type === ActionEventType.ServiceEnabled ||
      type === ActionEventType.ServiceDisabled ||
      type === ActionEventType.ServiceRegistered ||
      type === ActionEventType.ServiceUnregistered ||
      type === ActionEventType.ActionDataChanged
    );
  }

  private handleActionCompletion(
    actionCallId: ActionCallId | undefined,
    eventData: ServiceHubEventData<any>
  ) {
    if (!actionCallId) {
      this.logger.warn(
        `[@zoom/hybrid-jssdk] received a ZoomServiceHubEvent(type: ${eventData?.type}) without actionCallId`,
        eventData
      );
      return;
    }

    // when we call js service action, will receive two ZoomServiceHubEvent, first indicate whether request successfully. (type = 0)
    // second indicate the execution result from js side.
    if (this.jsActionResponseTracker.has(actionCallId)) {
      const firstAPICallResponse =
        this.jsActionResponseTracker.get(actionCallId) === false;
      if (firstAPICallResponse) {
        if (eventData.type === ActionEventType.ActionFailed) {
          this.jsActionResponseTracker.delete(actionCallId);
          this.rejectPendingAction(actionCallId, eventData);
        } else {
          this.jsActionResponseTracker.set(actionCallId, true);
        }
      } else {
        this.handleFinalActionResponse(actionCallId, eventData);
      }
    } else {
      this.handleFinalActionResponse(actionCallId, eventData);
    }
  }

  private handleFinalActionResponse(
    actionCallId: string,
    eventData: ServiceHubEventData<unknown>
  ) {
    this.jsActionResponseTracker.delete(actionCallId);
    if (eventData.type === ActionEventType.ActionCompleted) {
      this.resolvePendingAction(actionCallId, eventData);
    } else {
      this.rejectPendingAction(actionCallId, eventData);
    }
  }

  private resolvePendingAction(
    actionCallId: string,
    eventData: ServiceHubEventData<unknown>
  ) {
    const pendingAction = this.pendingActions.get(actionCallId);
    if (pendingAction) {
      pendingAction.resolve(eventData);
      this.pendingActions.delete(actionCallId);
    }
  }

  private rejectPendingAction(
    actionCallId: string,
    eventData: ServiceHubEventData<unknown>
  ) {
    const pendingAction = this.pendingActions.get(actionCallId);
    if (pendingAction) {
      pendingAction.reject(
        new Error(
          `[@zoom/hybrid-jssdk] service hub execution failed, actionCallId ${actionCallId} eventData: ${JSON.stringify(eventData)}`
        )
      );
      this.pendingActions.delete(actionCallId);
    }
  }

  private addServiceAction(action: ActionWithServiceId): void {
    const actions = this.allServices.get(action.serviceId) || [];
    if (!actions.find((a) => a.id === action.id)) {
      actions.push(action);
      this.allServices.set(action.serviceId, actions);
    }
  }

  private async refreshService(): Promise<void> {
    const response = await getAllServices();
    if (response.returnCode === 0 && response.result.services) {
      this.refreshAvailableService(response.result.services);
    }
  }

  private refreshAvailableService(services: ServiceHubServices): void {
    this.allServices.clear();

    services.forEach((service: ServiceHubService) => {
      service.actions.forEach((action) => {
        const platformVersion = action.availableInfo?.[this.platform!];
        if (
          platformVersion &&
          compareVersions(this.clientVersion, platformVersion) >= 0
        ) {
          this.addServiceAction({
            ...action,
            serviceId: service.serviceId,
          });
        }

        if (this.jsServiceIds.includes(service.serviceId)) {
          this.addServiceAction({
            ...action,
            serviceId: service.serviceId,
          });
        }
      });
    });
  }

  public async callAction<T, R = GeneralActionResponse>(
    action: ActionRequest<T>,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<ActionResponse<R>> {
    await this.initialized;

    const actionCallId = genRandomId('acid');
    const actionPromise = new Promise<ActionResponse<R>>((resolve, reject) => {
      this.pendingActions.set(actionCallId, { resolve, reject });
    });

    const available = await this.isActionAvailable(action);
    if (!available) {
      throw new ServiceHubError(
        action.service,
        action.actionId,
        `[@zoom/hybrid-jssdk] service hub action ${action.service}/${action.actionId} not available for ${this.platform} ${this.clientVersion}.`
      );
    }

    try {
      if (this.jsServiceIds.includes(action.service)) {
        this.jsActionResponseTracker.set(actionCallId, false);
      }
      const res = await executeAction({ ...action, actionCallId }, { timeout });
      if (!res || res.returnCode !== 0) {
        throw new ServiceHubError(
          action.service,
          action.actionId,
          `[@zoom/hybrid-jssdk] service hub executeAction ${action.service}/${action.actionId} failed. returnCode: ${res?.returnCode}`
        );
      }
    } catch (e) {
      this.jsActionResponseTracker.delete(actionCallId);
      const error = e as Error;
      throw new ServiceHubError(action.service, action.actionId, error.message);
    }

    return actionPromise;
  }

  /**
   * Calls a streaming service action that returns multiple responses
   * @param action The action to execute
   * @param listener The callback function to receive stream chunks
   * @param timeout Timeout in milliseconds
   * @returns A controller object that can be used to cancel the stream
   */
  public async callStreamingAction<
    T extends { service: ServiceId; actionId: ActionId; data?: any },
    S,
  >(
    action: T,
    listener: StreamListener<S>,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<StreamController> {
    await this.initialized;

    const actionCallId = genRandomId('sacid'); // Stream action call ID

    const available = await this.isActionAvailable(action);
    if (!available) {
      throw new ServiceHubError(
        action.service,
        action.actionId,
        `[@zoom/hybrid-jssdk] service hub action ${action.service}/${action.actionId} not available for ${this.platform} ${this.clientVersion}.`
      );
    }

    // Create a controller for the stream
    const controller: StreamController = {
      cancel: async () => {
        try {
          await postAppBridgeMessage(API.cancelStreamingResponse, {
            actionCallId,
          });
          this.streamListeners.delete(actionCallId);
        } catch (error) {
          this.logger.error('Failed to cancel stream', error);
        }
      },
    };

    // Register the stream listener
    this.streamListeners.set(actionCallId, {
      listener,
      controller,
    });

    try {
      const res = await postAppBridgeMessage(
        API.serviceHubExecuteAction,
        { ...action, actionCallId },
        { timeout }
      );

      if (!res || res.returnCode !== 0) {
        this.streamListeners.delete(actionCallId);
        throw new ServiceHubError(
          action.service,
          action.actionId,
          `[@zoom/hybrid-jssdk] streaming action ${action.service}/${action.actionId} failed to start. returnCode: ${res?.returnCode}`
        );
      }
    } catch (e) {
      this.streamListeners.delete(actionCallId);
      const error = e as Error;
      throw new ServiceHubError(action.service, action.actionId, error.message);
    }

    return controller;
  }

  /**
   * Registers a JavaScript service that can be called from native
   * @param service The service definition with handlers
   */
  public async registerJSService(service: JSService) {
    await this.initialized;

    if (this.allServices.has(service.serviceId)) {
      this.logger.warn(
        `Detected that service ${service.serviceId} has already been registered. Please avoid registering the same JS service across different WebView instances, as this may cause the registration to fail.`
      );
    }
    // Add to local registry
    this.jsServiceRegistry.set(service.serviceId, service);

    // Register with native
    const res = await postAppBridgeMessage(API.jsServiceRegister, {
      serviceId: service.serviceId,
      serviceName: toPascalCase(service.serviceId, ' '),
      actions: service.actions.map((action) => ({
        actionId: action.actionId,
      })),
    });
    if (!res || res.returnCode !== 0) {
      throw new ServiceHubError(
        service.serviceId,
        '',
        `[@zoom/hybrid-jssdk] service hub registerJSService ${service.serviceId} with actions ${service.actions.map((action) => action.actionId).join(',')} failed. returnCode: ${res.returnCode}`
      );
    }
  }

  /**
   * Unregisters a JavaScript service
   * @param serviceId The ID of the service to unregister
   */
  public async unregisterJSService(serviceId: string) {
    await this.initialized;

    if (this.jsServiceRegistry.has(serviceId)) {
      this.jsServiceRegistry.delete(serviceId);

      // Notify native
      const res = await postAppBridgeMessage(API.jsServiceUnregister, {
        serviceId,
      });
      if (!res || res.returnCode !== 0) {
        throw new ServiceHubError(
          serviceId,
          '',
          `[@zoom/hybrid-jssdk] service hub unregisterJSService ${serviceId} failed. returnCode: ${res?.returnCode}`
        );
      }
    } else {
      this.logger.warn(`You have not registered the service: ${serviceId}`);
    }
  }

  private async isActionAvailable(action: {
    service: ServiceId;
    actionId: ActionId;
  }): Promise<boolean> {
    await this.initialized;
    const actions = this.allServices.get(action.service);
    return Boolean(actions?.find((a) => a.id === action.actionId));
  }

  public async getService<T extends keyof typeof SERVICE_ID_MAP>(
    serviceType: T
  ): Promise<Services[T] | undefined> {
    await this.initialized;
    if (compareVersions(this.clientVersion, '6.4.0') >= 0) {
      const serviceId = SERVICE_ID_MAP[serviceType];
      if (!this.allServices.has(serviceId)) {
        const suggestion = CorrectTips[serviceType as CorrectTipsKey];
        this.logger.warn(
          `Service "${serviceType}" not found.${suggestion ? ` Did you mean "${suggestion}"?` : ''}`
        );
        return undefined;
      }

      return this.createServiceInstance(serviceType);
    } else {
      throw new ServiceHubError(
        serviceType,
        '',
        `[@zoom/hybrid-jssdk] service hub getService is not available for ${this.platform} ${this.clientVersion}.`
      );
    }
  }

  public async getAvailableServices(): Promise<
    Map<ServiceId, ActionWithServiceId[]>
  > {
    await this.initialized;
    return new Map(this.allServices);
  }

  /**
   * Returns a list of registered JavaScript services
   */
  public async getRegisteredJSServices(): Promise<JSService[]> {
    await this.initialized;
    return Array.from(this.jsServiceRegistry.values());
  }

  public onServiceChanged(onServiceChanged: ServiceChangedHandler | undefined) {
    this.onServiceChangedHandler = onServiceChanged;
  }

  public destroy() {
    unregisterSpecificNotificationHandler(
      'ZoomServiceHubEvent',
      this._handleServiceHubEvent
    );

    // Unregister JS service invocation handler
    unregisterSpecificNotificationHandler(
      'zmOnServiceHubJSAction',
      this._handleJSServiceInvocation
    );

    this.allServices.clear();
    this.pendingActions.clear();
    this.streamListeners.clear();
    this.jsServiceRegistry.clear();
    this.jsActionResponseTracker.clear();
    this.onServiceChangedHandler = undefined;
    this._handleServiceHubEvent = undefined;
    this._handleJSServiceInvocation = undefined;
  }

  private createServiceInstance<T extends keyof typeof SERVICE_ID_MAP>(
    serviceType: T
  ): Services[T] {
    const services: Services = {
      mail: {
        createMail: (payload: CreateMailData, option?: CallActionOption) =>
          this.callAction<CreateMailData>({
            service: 'mail-service',
            actionId: 'create-mail',
            data: payload,
            ...option,
          }),
      },
      calendar: {
        showScheduleMeeting: (
          payload: ShowScheduleMeetingData,
          option?: CallActionOption
        ) =>
          this.callAction<ShowScheduleMeetingData>({
            service: 'calendar-service',
            actionId: 'show-schedule-meeting',
            data: payload,
            ...option,
          }),
        getTodayUpcomingMeetingList: (option?: CallActionOption) =>
          this.callAction<EmptyActionData, GetTodayUpcomingMeetingListResponse>(
            {
              service: 'calendar-service',
              actionId: 'get-today-upcoming-meeting-list',
              data: {},
              ...option,
            }
          ),
      },
      chat: {
        sendMessageSkill: (
          payload: SendMessageSkillData,
          option?: CallActionOption
        ) =>
          this.callAction<SendMessageSkillData, SendMessageSkillResponse>({
            service: 'chat-service',
            actionId: 'send-message-skill',
            data: payload,
            ...option,
          }),
      },
      chatUI: {
        directShare: (payload: DirectShareData, option?: CallActionOption) =>
          this.callAction<DirectShareData>({
            service: 'chatui-service',
            actionId: 'direct_share',
            data: payload,
            ...option,
          }),
        directShareCheck: (option?: CallActionOption) =>
          this.callAction<EmptyActionData, DirectShareCheckResponse>({
            service: 'chatui-service',
            actionId: 'direct_share_check',
            data: {},
            ...option,
          }),
        showSettingPage: (
          payload: ShowSettingPageData,
          option?: CallActionOption
        ) =>
          this.callAction<ShowSettingPageData>({
            service: 'chatui-service',
            actionId: 'show-setting-page',
            data: payload,
            ...option,
          }),
        createNewChat: (data: any = {}, option?: CallActionOption) =>
          this.callAction<EmptyActionData>({
            service: 'chatui-service',
            actionId: 'create_new_chat',
            data,
            ...option,
          }),
      },
      zoomApp: {
        showZappTab: (option?: CallActionOption) =>
          this.callAction<EmptyActionData>({
            service: 'zapp-service',
            actionId: 'show-zapp-tab',
            data: {},
            ...option,
          }),
        getFrontZappDetailInfo: (option?: CallActionOption) =>
          this.callAction<EmptyActionData, GetFrontZappDetailInfoResponse>({
            service: 'zapp-service',
            actionId: 'get-front-zapp-detail-info',
            data: {},
            ...option,
          }),
      },
      doc: {
        newDoc: (payload: NewDocData, option?: CallActionOption) =>
          this.callAction<NewDocData>({
            service: 'doc-service',
            actionId: 'new-doc',
            data: payload,
            ...option,
          }),
        showDocTab: (option?: CallActionOption) =>
          this.callAction<EmptyActionData>({
            service: 'doc-service',
            actionId: 'show-doc-tab',
            data: {},
            ...option,
          }),
        docDraftPreview: (
          payload: DocDraftPreviewData,
          option?: CallActionOption
        ) =>
          this.callAction<DocDraftPreviewData>({
            service: 'doc-service',
            actionId: 'doc-draft-preview',
            data: payload,
            ...option,
          }),
      },
      whiteboard: {
        showWhiteboardTab: (option?: CallActionOption) =>
          this.callAction<EmptyActionData>({
            service: 'whiteboard-service',
            actionId: 'show-whiteboard-tab',
            data: {},
            ...option,
          }),
        getCurrentOpenedWhiteboardInfo: (option?: CallActionOption) =>
          this.callAction<
            EmptyActionData,
            GetCurrentOpenedWhiteboardInfoResponse
          >({
            service: 'whiteboard-service',
            actionId: 'get-current-opened-whiteboard-info',
            data: {},
            ...option,
          }),
        newWhiteboard: (
          payload: NewWhiteboardData,
          option?: CallActionOption
        ) =>
          this.callAction<NewWhiteboardData>({
            service: 'whiteboard-service',
            actionId: 'new-whiteboard',
            data: payload,
            ...option,
          }),
      },
      tasks: {
        createTask: (payload: CreateTasksData, option?: CallActionOption) =>
          this.callAction<CreateTasksData>({
            service: 'tasks-service',
            actionId: 'create-tasks',
            data: payload,
            ...option,
          }),
        viewTasks: (payload: ViewTasksData, option?: CallActionOption) =>
          this.callAction<ViewTasksData>({
            service: 'tasks-service',
            actionId: 'view-tasks',
            data: payload,
            ...option,
          }),
      },
      telemetry: {
        trackClientInteract: (
          payload: TrackClientInteractData,
          option?: CallActionOption
        ) =>
          this.callAction<TrackClientInteractData>({
            service: 'telemetry-service',
            actionId: 'track-client-interact',
            data: payload,
            ...option,
          }),
      },
      deeplink: {
        showDiagnosticWindow: (option?: CallActionOption) =>
          this.callAction<EmptyActionData>({
            service: 'deeplink-service',
            actionId: 'show-dignostic-window',
            data: {},
            ...option,
          }),
      },
      common: {
        popoutMenu: (payload: PopoutMenuData, option?: CallActionOption) =>
          this.callAction<PopoutMenuData, PopoutMenuResponse>({
            service: 'common-service',
            actionId: 'popout-menu',
            data: payload,
            ...option,
          }),
        showAlert: (payload: ShowAlertData, option?: CallActionOption) =>
          this.callAction<ShowAlertData, ShowAlertResponse>({
            service: 'common-service',
            actionId: 'show-alert',
            data: payload,
            ...option,
          }),
      },
    };

    return services[serviceType];
  }
}

class ServiceHubManager {
  private static instance: ServiceHubManager;
  private serviceHub: ServiceHub | null = null;

  private constructor() {}

  public static getInstance(): ServiceHubManager {
    if (!ServiceHubManager.instance) {
      ServiceHubManager.instance = new ServiceHubManager();
    }
    return ServiceHubManager.instance;
  }

  private getServiceHub(): ServiceHub {
    if (!this.serviceHub) {
      this.serviceHub = new ServiceHub();
    }
    return this.serviceHub;
  }

  public getService<T extends keyof typeof SERVICE_ID_MAP>(serviceType: T) {
    return this.getServiceHub().getService(serviceType);
  }

  public async getAvailableServices() {
    return this.getServiceHub().getAvailableServices();
  }

  public callStreamingAction<
    T extends { service: ServiceId; actionId: ActionId; data?: any },
  >(action: T, listener: StreamListener, timeout?: number) {
    return this.getServiceHub().callStreamingAction(action, listener, timeout);
  }

  public callAction<T, R>(action: ActionRequest<T>) {
    return this.getServiceHub().callAction<T, R>(action);
  }

  public registerJSService(service: JSService) {
    return this.getServiceHub().registerJSService(service);
  }

  public unregisterJSService(serviceId: string) {
    return this.getServiceHub().unregisterJSService(serviceId);
  }

  public getRegisteredJSServices() {
    return this.getServiceHub().getRegisteredJSServices();
  }

  public onServiceChanged(handler: ServiceChangedHandler) {
    this.getServiceHub().onServiceChanged(handler);
  }

  public offServiceChanged() {
    this.getServiceHub().onServiceChanged(undefined);
  }

  public destroy() {
    if (this.serviceHub) {
      this.serviceHub.destroy();
      this.serviceHub = null;
    }
  }
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getService
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
// Export enhanced API functions
const getService = <T extends keyof typeof SERVICE_ID_MAP>(serviceType: T) =>
  ServiceHubManager.getInstance().getService(serviceType);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getAvailableServices
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const getAvailableServices = async () =>
  await ServiceHubManager.getInstance().getAvailableServices();

/**
 * Calls a streaming action that will return multiple responses
 * @param action The action details including service, actionId and data
 * @param listener A callback function that receives stream chunks
 * @param timeout Optional timeout in milliseconds
 * @returns A controller object that can cancel the stream
 */
const callStreamingAction = <T>(
  action: ActionRequest<T>,
  listener: StreamListener,
  timeout?: number
) =>
  ServiceHubManager.getInstance().callStreamingAction(
    action,
    listener,
    timeout
  );

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.callAction
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const callAction = <T = unknown, R = unknown>(action: ActionRequest<T>) =>
  ServiceHubManager.getInstance().callAction<T, R>(action);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.registerJSService
 *
 * [PLATFORM]
 * mac        6.5.0+
 * windows    6.5.0+
 * linux      6.5.0+
 * ios        6.5.0+
 * android    6.5.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const registerJSService = (service: JSService) =>
  ServiceHubManager.getInstance().registerJSService(service);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.unregisterJSService
 *
 * [PLATFORM]
 * mac        6.5.0+
 * windows    6.5.0+
 * linux      6.5.0+
 * ios        6.5.0+
 * android    6.5.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const unregisterJSService = (serviceId: string) =>
  ServiceHubManager.getInstance().unregisterJSService(serviceId);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getRegisteredJSServices
 *
 * [PLATFORM]
 * mac        6.5.0+
 * windows    6.5.0+
 * linux      6.5.0+
 * ios        6.5.0+
 * android    6.5.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const getRegisteredJSServices = () =>
  ServiceHubManager.getInstance().getRegisteredJSServices();

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.onServiceChanged
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const onServiceChanged = (handler: ServiceChangedHandler) =>
  ServiceHubManager.getInstance().onServiceChanged(handler);

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.offServiceChanged
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const offServiceChanged = () =>
  ServiceHubManager.getInstance().offServiceChanged();

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.destroy
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
const destroy = () => ServiceHubManager.getInstance().destroy();

export {
  getService,
  callAction,
  getAvailableServices,
  callStreamingAction,
  registerJSService,
  unregisterJSService,
  getRegisteredJSServices,
  onServiceChanged,
  offServiceChanged,
  destroy,
};
